import React, { useCallback, useEffect, useReducer, useRef, useState} from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Divider,
  Grid,
  Button,
  Collapse,
  Typography,
  Stack,
  Chip,
  Modal,
  IconButton,
} from "@mui/material"
import { ArrowDownward, ArrowUpward, KeyboardArrowDown, KeyboardArrowUp, DeleteOutline, WarningAmber, Close } from '@mui/icons-material';
import { userDetailsType, SubscriptionFilterType, subscriptionStatusFilterOptions, subscriptionStatusStringType, SortByType, SortType, 
SubscriptionOptions, FormatLinkedAccountID, FormatLinkedAccountType, RangeFilterType, RangeFilterOptions, SearchFilterOptions, searchFilterType,
dateRangeFilter, SubscriptionTierOptions, SubscriptionTierFilterType, InviteStatusStringType, InviteStatusOptions, BillingStatusStringType, BillingStatusOptions} from '../Types';
import { LookerReportsErrors } from '../../../shared/molecule/LookerReportsErrors';
import {getFilters, getRangeFilters, formatFromReportDateString, getNumberRangeFilters, formatDataArray, formatAddressesArray, getSubscriptionStatusStyle, getInviteStatusStyle, getBillingStatusStyle } from '../../../../utils/helpers';
import Autocomplete from '@mui/material/Autocomplete';
import CustomDatePicker from '../../../shared/atom/inputs/DatePicker';
import dayjs from 'dayjs'
import CustomTablePagination from "../../../shared/atom/TablePagination/TablePagination"
import { createPaginatedReportApi, PaginatedReportArgs, PaginatedReportReturn, createSearchSuggestionsApi, removeUser, sendOtpEvent} from '../../../../redux/corporateCodeService';
import { useAppDispatch, useAppSelector } from '../../../../redux/storeExports';
import {CircularProgress, useMediaQuery} from '@mui/material';
import OtpInput from 'react-otp-input';
import { selectAccountEmail } from '@meprism/shared/src/redux/authentication/authenticationSlice';

type DialogState = {
    open: boolean
    userToDelete: userDetailsType | null
    isDeleting: boolean
    otpSent: boolean
    otpCode: string
    isSendingOtp: boolean
    otpSecondsLeft: number
}

type DialogAction =
    | { type: 'OPEN'; payload: userDetailsType }
    | { type: 'CLOSE' }
    | { type: 'DELETE_START' }
    | { type: 'DELETE_SUCCESS' }
    | { type: 'DELETE_ERROR' }
    | { type: 'OTP_SENDING' }
    | { type: 'OTP_SENT'; payload: number }
    | { type: 'OTP_SEND_ERROR' }
    | { type: 'SET_OTP_CODE'; payload: string }
    | { type: 'TICK' }

const dialogInitialState: DialogState = {
    open: false,
    userToDelete: null,
    isDeleting: false,
    otpSent: false,
    otpCode: '',
    isSendingOtp: false,
    otpSecondsLeft: 0,
}

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
    switch (action.type) {
        case 'OPEN':
            return { ...dialogInitialState, open: true, userToDelete: action.payload }
        case 'CLOSE':
            return { ...dialogInitialState }
        case 'DELETE_START':
            return { ...state, isDeleting: true }
        case 'DELETE_SUCCESS':
            return { ...dialogInitialState }
        case 'DELETE_ERROR':
            return { ...state, isDeleting: false }
        case 'OTP_SENDING':
            return { ...state, isSendingOtp: true }
        case 'OTP_SENT':
            return { ...state, isSendingOtp: false, otpSent: true, otpSecondsLeft: action.payload }
        case 'OTP_SEND_ERROR':
            return { ...state, isSendingOtp: false }
        case 'SET_OTP_CODE':
            return { ...state, otpCode: action.payload }
        case 'TICK':
            return { ...state, otpSecondsLeft: Math.max(state.otpSecondsLeft - 1, 0) }
        default:
            return state
    }
}

type UserReportProps = {
  userReportSyncTime : string
};

export const UserDetailsTab = ({userReportSyncTime}: UserReportProps) =>{
    const dispatch = useAppDispatch()
    const adminEmail = useAppSelector(selectAccountEmail)
    const isSmallScreen = useMediaQuery('(max-width:425px)')
    const isXSmallScreen = useMediaQuery('(max-width:320px)')
    const [userDetails, setUserDetails] = useState<userDetailsType[]>([])
    const [searchFilter, setSearchFilter] = useState<searchFilterType>('Muid')    
    const [isLoading, setIsLoading] = useState(true)
    const [searchValue, setSearchValue] = useState('')
    const [sortType, setSortType] = useState<SortType>('DESC') 
    const [sortBy, setSortBy] = useState<SortByType>('create_time')
    const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilterType[]>([])
    const [subscriptionTierFilter, setSubscriptionTierFilter] = useState<SubscriptionTierFilterType[]>([])    
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<subscriptionStatusStringType[]>([])
    const [inviteStatusFilter, setInviteStatusFilter] = useState<InviteStatusStringType[]>([])
    const [billingStatusFilter, setBillingStatusFilter] = useState<BillingStatusStringType[]>([])
    const [expandedMuid, setExpandedMuid] = useState('')
    const [startDate, setStartDate] = useState<dayjs.Dayjs | undefined>(undefined)
    const [endDate, setEndDate] = useState<dayjs.Dayjs | undefined>(undefined)
    const [rangeFilter, setRangeFilter] = useState<RangeFilterType | null>(null)
    const [expiringDaysFilter, setExpiringDaysFilter] = useState<number | null>(null)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [hasNext, setHasNext] = useState(false)
    const [refetch, setRefetch] = useState(true)
    const [fetchNextPage, setFetchNextPage] = useState(false)
    const [newPage, setNewPage] = useState(0)
    const [offset, setOffset] = useState(0)
    const [prevSearchValue, setPrevSearchValue] = useState('')
    const [searchOptions, setSearchOptions] = useState<string[]>([])
    const [searchOffset, setSearchOffset] = useState(0)
    const [searchHasNext, setSearchHasNext] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false)
    const [getNextSearchResults, setGetNextSearchResults] = useState(false)
    const debouncedSearchValue = useDebounce(searchValue, 500)
    const [searchDropDownOpen, setSearchDropDownOpen] = useState(false);
    const [sortStyle, setSortStyle] = useState({'email':'ASC', 'name':'ASC', 'expiring_in_days':'ASC', 'create_time':'DESC', 'update_time':'ASC', 'subscription':'ASC', 'subscription_tier': 'ASC'})
    const [dateRangeFilterErrors, setDateRangeFilterErrors] = useState(dateRangeFilter)
    const [dialog, dialogDispatch] = useReducer(dialogReducer, dialogInitialState)
    const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const OTP_DURATION = 300 // 5 minutes in seconds
    const ToggleSort = (sortByValue: SortByType) => {
        const newSort = sortStyle[sortByValue] === 'ASC' ? 'DESC' : 'ASC'
        setSortStyle({ ...sortStyle, [sortByValue]: newSort })
        setSortType(newSort)
        setSortBy(sortByValue)
        setRefetch(true)
    }

    const getSortStyle = (sortByValue: SortByType) => {
        const Icon = sortStyle[sortByValue] === 'DESC' ? ArrowDownward : ArrowUpward;
        const color = sortBy === sortByValue ? 'error' : 'primary';
        return <Icon color={color}/>;
    };

    const onSearch = () => {        
        const checkDateRangeError = Boolean(startDate || endDate || rangeFilter);
        if(checkDateRangeError){
            const newErrors = {
                startDate: !startDate,
                endDate: !endDate,
                dateFilter: !rangeFilter,
            };
            const hasError = Object.values(newErrors).some(value => value === true);
            setDateRangeFilterErrors(newErrors);
            if (hasError) return
        }
        setDateRangeFilterErrors(dateRangeFilter)
        setRefetch(true);
    };

    function useDebounce(value: string, delay: number) {
        const [debouncedValue, setDebouncedValue] = useState(value);

            useEffect(() => {
                const handler = setTimeout(() => {
                setDebouncedValue(value);
                }, delay);

                return () => {
                clearTimeout(handler);
                };
            }, [value, delay]);

            return debouncedValue;
    }

    const colStyle = {
        fontSize: '16px',
        whiteSpace: 'nowrap',
        minWidth: 'max-content',
        maxWidth: 'max-content'
    }

    const formatAddresses=(addresses: string | null)=>{
        const formattedAddresses = formatAddressesArray((addresses && addresses!=='[]')? addresses : '')
        return(
            <Box sx={{display:"flex", flexDirection:"column"}}>
                {(!addresses || addresses==='[]') ?
                     <Paper sx={{display:'flex', alignItems:'center', minHeight:'100%',px:'20px', ...colStyle}}>
                        <Typography variant="body2" fontWeight={'bold'}>
                            Address :&nbsp;&nbsp;
                        </Typography>
                        <Typography variant="body2">
                            None
                        </Typography>
                    </Paper>:
                    <Paper sx={{display:'flex', alignItems:'flex-start', flexDirection:"column", minHeight:'100%', justifyContent:'center', px:'20px', ...colStyle}}>
                        {formattedAddresses.split(';').map((value: string, index: number)=>
                            <Box sx={{display:'flex', ...colStyle, my:2}}>
                                <Typography variant="body2" fontWeight={'bold'}>
                                    Address {formattedAddresses.split(';').length!==1 && `- ${index+1}`} :&nbsp;&nbsp;
                                </Typography>
                                <Typography variant="body2">
                                    {value}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                }
            </Box>
        )
    }
    const formatStringArray=(array: string | null, label:string)=>{
        const formattedData = formatDataArray((array && array!=='[]')? array : '')
        return(
            <Box sx={{display:"flex", flexDirection:"column"}}>
                {(!array || array==='[]') ?
                     <Paper sx={{display:'flex', alignItems:'center', minHeight:'100%',px:'20px', ...colStyle}}>
                        <Typography variant="body2" fontWeight={'bold'}>
                            {label}s :&nbsp;&nbsp;
                        </Typography>
                        <Typography variant="body2">
                            None
                        </Typography>
                    </Paper>
                :
                    <Paper sx={{display:'flex', alignItems:'flex-start', flexDirection:"column", minHeight:'100%', justifyContent:'center', px:'20px', ...colStyle}}>
                        {formattedData.split(',').map((value: string, index: number)=>
                            <Box sx={{display:'flex', ...colStyle, my:2}}>
                                <Typography variant="body2" fontWeight={'bold'}>
                                    {label} {formattedData.split(',').length!==1 && `- ${index+1}`} :&nbsp;&nbsp;
                                </Typography>
                                <Typography variant="body2">
                                    {value}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                }
            </Box>
        )
    }

     useEffect(()=>{
            const fetchUsersReport= async()=>{
                setIsLoading(true)
                let newOffset = 0;
                try{
                    if(fetchNextPage){
                        if(newPage < page){
                            newOffset = Math.max(offset - pageSize, 0)
                            setPage(page -1)
                        }else{
                            newOffset = pageSize + offset
                            setPage(page +1)
                    }
                    }
                    const searchTrimmed = searchValue? searchValue.trim():''
                    let searchFilterKey = searchFilter.toLowerCase()
                    if(searchFilterKey.includes('name')){
                        searchFilterKey = 'name'
                    } 
                    const params : PaginatedReportArgs = {
                    "table" : "user_reporting",
                    "page_size": pageSize,
                    "sort_by": sortBy,
                    "offset" : fetchNextPage? newOffset : 0, 
                    "order" : sortType,
                    "Filters" : {
                        "filters": getFilters({
                            "subscription": subscriptionFilter, 
                            "subscription_status": subscriptionStatusFilter, 
                            "subscription_tier": subscriptionTierFilter,
                            "invite_status": inviteStatusFilter,
                            "billing_status": billingStatusFilter
                        }),
                        "search_filter" : !searchTrimmed ? {}: {[searchFilterKey]:searchTrimmed},
                        "date_range_filter": getRangeFilters([{rangeFilter, startDate, endDate}]),
                        "number_range_filter": getNumberRangeFilters([{key: 'expiring_in_days', startValue: 0, endValue:expiringDaysFilter}])
                        }
                    }
                    const response : PaginatedReportReturn = await dispatch(createPaginatedReportApi(params)).unwrap()
                    setUserDetails(response.data)
                    setHasNext(response.has_next)
                }
                catch(e){
                    console.log('error getting looker users report: ', e)
                }finally{
                    setIsLoading(false)
                    if (fetchNextPage) {
                        setOffset(newOffset)
                    } else {
                        setOffset(0)
                    }
                    setFetchNextPage(false)
                }
            }
            if(refetch){
                if(!fetchNextPage){
                    setPage(1)
                }
                fetchUsersReport()
            }
            setRefetch(false)
        },[refetch, expiringDaysFilter, subscriptionFilter, subscriptionStatusFilter, searchValue, rangeFilter, startDate, endDate, dispatch,
            pageSize, sortBy, sortType, offset, fetchNextPage, newPage, page, searchFilter, subscriptionTierFilter, inviteStatusFilter, billingStatusFilter
        ])

    const getNextPage = (newPage: number)=>{
        setNewPage(newPage)
        setFetchNextPage(true)
        setRefetch(true)
    }
    
    const setNewPageSize = (newPageSize: number)=>{
        setPageSize(newPageSize)
        setRefetch(true)
    }

    const getSearchOptions = useCallback(async()=>{
        setSearchLoading(true)
        if(!getNextSearchResults){
            setSearchOptions([])
        }
        setPrevSearchValue(debouncedSearchValue)
        const key = searchFilter.toLowerCase().replace(/\s+/g, '_');
        const params = {
            'value': debouncedSearchValue.trim(),
            'key': key,
            'page_size':15,
            'offset': getNextSearchResults? searchOffset: 0
        }
        const results = await dispatch(createSearchSuggestionsApi(params)).unwrap()
        
        getNextSearchResults? setSearchOptions([...searchOptions, ...results.result]) : setSearchOptions(results.result)
        setSearchHasNext(results.has_next)
        setGetNextSearchResults(false)
        setSearchLoading(false)
    },[debouncedSearchValue, dispatch, searchFilter, searchOffset, searchOptions, getNextSearchResults])

    useEffect(()=>{
        if(startDate && !endDate){
            setEndDate(dayjs())
        }
    }, [startDate, endDate])

    useEffect(()=>{
        const searchFlag = prevSearchValue===debouncedSearchValue
        if(searchDropDownOpen && (getNextSearchResults || (debouncedSearchValue.length > 2 && !searchFlag))){
            getSearchOptions()
        }
    },[debouncedSearchValue, getSearchOptions, searchDropDownOpen, prevSearchValue, getNextSearchResults])

    useEffect(() => {
        if (dialog.otpSecondsLeft === 0 && otpTimerRef.current) {
            clearInterval(otpTimerRef.current)
            otpTimerRef.current = null
        }
    }, [dialog.otpSecondsLeft])

    useEffect(() => {
        return () => {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current)
        }
    }, [])

    const otpExpired = dialog.otpSent && dialog.otpSecondsLeft === 0
    const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

    const handleDeleteClick = (user: userDetailsType) => {
        dialogDispatch({ type: 'OPEN', payload: user })
    }

    const handleInitiateRemoval = async () => {
        if (!adminEmail) return
        dialogDispatch({ type: 'OTP_SENDING' })
        try {
            await dispatch(sendOtpEvent()).unwrap()
            if (otpTimerRef.current) clearInterval(otpTimerRef.current)
            dialogDispatch({ type: 'OTP_SENT', payload: OTP_DURATION })
            otpTimerRef.current = setInterval(() => {
                dialogDispatch({ type: 'TICK' })
            }, 1000)
        } catch (error) {
            console.error('Error sending OTP:', error)
            dialogDispatch({ type: 'OTP_SEND_ERROR' })
        }
    }

    const handleDeleteConfirm = async () => {
        if (!dialog.userToDelete || !adminEmail) return
        dialogDispatch({ type: 'DELETE_START' })
        try {
            await dispatch(removeUser({
                target_muid: dialog.userToDelete.muid,
                otp_code: dialog.otpCode,
                target_email: dialog.userToDelete.email || undefined
            })).unwrap()
            dialogDispatch({ type: 'DELETE_SUCCESS' })
            setRefetch(true)
        } catch (error) {
            console.error('Error deleting user:', error)
            dialogDispatch({ type: 'DELETE_ERROR' })
        }
    }

    const handleDeleteCancel = () => {
        dialogDispatch({ type: 'CLOSE' })
        if (otpTimerRef.current) {
            clearInterval(otpTimerRef.current)
            otpTimerRef.current = null
        }
    }

    return(
        <Box width={'100%'}>
            {isLoading?
                <Box
                    sx={{
                        width: '100%',
                        height: '50vh',
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                    <CircularProgress sx={{ alignSelf: 'center', justifySelf: 'center' }} />
                </Box>
                :
                <>
                    <Box sx={{display:'flex', alignItems:'center', justifyContent:'center'}}>                
                        <Grid container spacing={4} mt={5} mb={10} width={'80vw'}>
                            <Grid item lg={12} md={12} xs={12} mb={5} sx={{display:'flex'}}>
                                <Box
                                    sx={{border:'1px green solid', backgroundColor:"white", py:3, px:2, borderRadius:2, color:"green"}}
                                >
                                    <Typography variant='h4' sx={{color:'success.main'}}>Last Data Sync: {userReportSyncTime}</Typography>
                                </Box>
                            </Grid>
                            
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    multiple
                                    options={SubscriptionOptions}
                                    value={subscriptionFilter}
                                    onChange={(event, newValue) => {
                                        setSubscriptionFilter(newValue);
                                    }}
                                    limitTags={1}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Subscription Type"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option} {...getTagProps({ index })} />
                                    ))
                                    }
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    multiple
                                    options={subscriptionStatusFilterOptions}
                                    value={subscriptionStatusFilter}
                                    onChange={(event, newValue) => {
                                        setSubscriptionStatusFilter(newValue);
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Subscription Status"
                                    />
                                    )}
                                    fullWidth
                                    limitTags={1}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option} {...getTagProps({ index })} />
                                    ))
                                    }
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                            <Autocomplete
                                multiple
                                options={SubscriptionTierOptions}
                                value={subscriptionTierFilter}
                                onChange={(event, newValue) => {
                                    setSubscriptionTierFilter(newValue);
                                }}
                                clearOnEscape
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Subscription Tier"
                                />
                                )}
                                fullWidth
                                limitTags={1}
                                isOptionEqualToValue={(option, value) => option === value}
                                renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} {...getTagProps({ index })} />
                                ))
                                }
                            />
                        </Grid>
                        <Grid item lg={4} md={6} xs={12}>
                            <Autocomplete
                                multiple
                                options={InviteStatusOptions}
                                value={inviteStatusFilter}
                                onChange={(event, newValue) => {
                                    setInviteStatusFilter(newValue);
                                }}
                                clearOnEscape
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Invite Status"
                                />
                                )}
                                fullWidth
                                limitTags={1}
                                isOptionEqualToValue={(option, value) => option === value}
                                renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} {...getTagProps({ index })} />
                                ))
                                }
                            />
                        </Grid>
                        <Grid item lg={4} md={6} xs={12}>
                            <Autocomplete
                                multiple
                                options={BillingStatusOptions}
                                value={billingStatusFilter}
                                onChange={(event, newValue) => {
                                    setBillingStatusFilter(newValue);
                                }}
                                clearOnEscape
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Billing Status"
                                />
                                )}
                                fullWidth
                                limitTags={1}
                                isOptionEqualToValue={(option, value) => option === value}
                                renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} {...getTagProps({ index })} />
                                ))
                                }
                            />
                        </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={RangeFilterOptions}
                                    value={rangeFilter}
                                    onChange={(event, newValue) => {
                                        //@ts-ignore
                                        setRangeFilter(newValue);
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filter By Date"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                                {
                                    dateRangeFilterErrors['dateFilter'] && <LookerReportsErrors errorKey='date type' filter='date'/>
                                }
                            </Grid>
                           <Grid item lg={4} md={6} xs={12}>
                            <CustomDatePicker
                                label="Start Date"
                                value={startDate? startDate : null}
                                onChange={(value)=>{value? setStartDate(value): setStartDate(undefined)}}
                                disableFuture
                                clearable
                                maxDate={endDate}
                            />
                            {dateRangeFilterErrors['startDate'] && (
                                <LookerReportsErrors errorKey="start date" filter="date" />
                            )}
                            </Grid>

                            <Grid item lg={4} md={6} xs={12}>
                            <CustomDatePicker
                                label="End Date"
                                value={endDate? endDate : null}
                                onChange={(value)=>{value? setEndDate(value): setEndDate(undefined)}}
                                disableFuture
                                clearable
                                minDate={startDate}
                            />
                            {dateRangeFilterErrors['endDate'] && (
                                <LookerReportsErrors errorKey="end date" filter="date" />
                            )}
                            </Grid>

                            <Grid item lg={4} md={6} xs={12}>
                               <TextField
                                    type="text"
                                    value={
                                        expiringDaysFilter !== null && !isNaN(expiringDaysFilter)
                                        ? expiringDaysFilter.toString()
                                        : ""
                                    }
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const rawValue = event.target.value;
                                        if (rawValue === "") {
                                            setExpiringDaysFilter(null);
                                            return;
                                        }
                                        if (rawValue.length > 1 && rawValue.startsWith("0")) {
                                            return;
                                        }
                                        if (!/^\d{1,5}$/.test(rawValue)) {
                                            return;
                                        }
                                        const newValue = parseInt(rawValue, 10);
                                        if (!isNaN(newValue) && newValue >= 0 && newValue <= 99999) {
                                            setExpiringDaysFilter(newValue);
                                        }
                                    }}
                                    inputProps={{ inputMode: "numeric", maxLength: 5 }}
                                    label="Expiring In Days"
                                    size="small"
                                    fullWidth
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options = {!searchValue.trim()? []:
                                        searchHasNext? [...searchOptions, "Load More"]: searchOptions} 
                                    value={searchValue}
                                    open={searchDropDownOpen}
                                    disableClearable
                                    freeSolo
                                    filterOptions={(x) => (searchValue && searchValue.length>2 && x.length===0 && !searchLoading) 
                                        ? ['No Match Found']:x}
                                    onOpen={()=>setSearchDropDownOpen(true)}
                                    onClose={()=>{setSearchDropDownOpen(false)
                                            setSearchOptions([])
                                            setOffset(0)
                                            setPrevSearchValue('')
                                            setSearchHasNext(false)
                                            setSearchLoading(false)
                                        }
                                    }
                                    onInputChange={(event, newValue) => {
                                        if (newValue !== "Load More" && newValue!=='No Match Found') setSearchValue(newValue);
                                    }}

                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        label="Search User"
                                        />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    
                                    ListboxProps={{
                                        style: {
                                        maxHeight: '30vh',
                                        overflowY:'auto'
                                        }}}
                                    loading={searchLoading}
                                    loadingText="Loading..."
                                    renderOption={(props, option) => {
                                        if (option === "Load More") {
                                            return (
                                                <li {...props} style={{ justifyContent: "center" }}>
                                                <Button
                                                    fullWidth
                                                    disabled={searchLoading}
                                                    onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSearchOffset((prev) => prev + 15);
                                                    setGetNextSearchResults(true);
                                                    }}
                                                    sx={{
                                                    backgroundColor:"transparent !important",
                                                    fontWeight: "bold",
                                                    textAlign: "center",
                                                    
                                                    }}
                                                >
                                                    {searchLoading ? "Loading..." : "Load More"}
                                                </Button>
                                                </li>
                                            );
                                            }
                                        if (option === "No Match Found") {
                                            return (
                                                    <Button disabled={true}
                                                    fullWidth
                                                    sx={{
                                                    backgroundColor:"transparent !important",
                                                    fontWeight: "bold",
                                                    textAlign: "center",
                                                    cursor:'none'
                                                    }}
                                                    >
                                                        {option}
                                                    </Button>
                                            );
                                            }
                                        return <li 
                                          style={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word"
                                            }}
                                        {...props}>{option}</li>;
                                    }}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={SearchFilterOptions}
                                    value={searchFilter}
                                    disableClearable
                                    onChange={(event, newValue) => {
                                        setSearchFilter(newValue as searchFilterType);
                                    }}
                                    defaultValue={'Muid'}
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search By"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => onSearch()}
                                    sx={{minWidth: '50%', height: '100%'}}                                                    
                                >
                                    Search
                                </Button>
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}/>
                        </Grid>
                    </Box>
                    <Divider sx={{mt: 5, mb: 5}}/>
                    <TableContainer component={Paper} style={{ marginTop: 20 }}>
                        <Table sx={{ minWidth: 1400, overflow: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>MUID</TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Email
                                            <Button onClick={(e: React.MouseEvent)=>
                                                    {
                                                        setSortBy('email')
                                                        ToggleSort('email')
                                                    }}
                                            >
                                                {getSortStyle('email')}
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Name
                                            <Button onClick={(e: React.MouseEvent)=>
                                                    {
                                                        setSortBy('name')
                                                        ToggleSort('name')
                                                    }}
                                            >
                                                    {getSortStyle('name')}
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell>Invite Status</TableCell>
                                    <TableCell>Subscription
                                        <Button onClick={(e: React.MouseEvent)=>
                                                    {
                                                        setSortBy('subscription')
                                                        ToggleSort('subscription')
                                                    }}
                                            >
                                                    {getSortStyle('subscription')}
                                            </Button>
                                    </TableCell>
                                    <TableCell>Subscription Status</TableCell>
                                    <TableCell>Subscription Tier
                                        <Button onClick={(e: React.MouseEvent)=>
                                                    {
                                                        setSortBy('subscription_tier')
                                                        ToggleSort('subscription_tier')
                                                    }}
                                            >
                                                    {getSortStyle('subscription_tier')}
                                            </Button>
                                    </TableCell>
                                    <TableCell>Billing Status</TableCell>
                                    <TableCell>Expiring In (Days)
                                        <Button onClick={(e: React.MouseEvent)=>
                                                {
                                                    setSortBy('expiring_in_days')
                                                        ToggleSort('expiring_in_days')
                                                }}
                                            >
                                                {getSortStyle('expiring_in_days')}
                                            </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Created At
                                            <Button onClick={(e: React.MouseEvent)=>
                                                        {
                                                            setSortBy('create_time')
                                                            ToggleSort('create_time')
                                                        }}
                                                    >
                                                    {getSortStyle('create_time')}
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{display:'flex'}}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Updated At
                                            <Button onClick={(e: React.MouseEvent)=>
                                                    {
                                                        setSortBy('update_time')
                                                        ToggleSort('update_time')
                                                    }}
                                            >
                                                {getSortStyle('update_time')}
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { Array.isArray(userDetails) &&
                                    userDetails.length===0 &&
                                    <TableRow>
                                        <TableCell colSpan={13} sx={{textAlign:"center"}}>
                                            No Match Found
                                        </TableCell>
                                    </TableRow>
                                }
                                {Array.isArray(userDetails) && userDetails.length!==0 && userDetails
                                    .map((user: userDetailsType, index: number) => (
                                    <>
                                    <TableRow
                                        key={index}
                                         sx={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff"}}
                                    >
                                        <TableCell>
                                            <Button onClick={()=>expandedMuid===user.muid?setExpandedMuid(''):setExpandedMuid(user.muid)}>
                                                {expandedMuid===user.muid?
                                                <KeyboardArrowUp/> :
                                                <KeyboardArrowDown/>}
                                            </Button>
                                        </TableCell>
                                        <TableCell sx={colStyle}>{user.muid}</TableCell>
                                        <TableCell sx={colStyle}>{user.email? user.email : '-'}</TableCell>
                                        <TableCell sx={colStyle}>{user.name? user.name: '-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200, ...getInviteStatusStyle(user.invite_status)}}>{user.invite_status? user.invite_status : '-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200}}>{user.subscription? user.subscription : '-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200, ...getSubscriptionStatusStyle(user.subscription_status)}}>{user.subscription_status? user.subscription_status:'Free'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200}}>{user.subscription_tier? user.subscription_tier : '-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200, ...getBillingStatusStyle(user.billing_status)}}>{user.billing_status? user.billing_status : '-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200}}>{user.expiring_in_days? user.expiring_in_days:'-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200}}>{user.create_time? formatFromReportDateString(user.create_time):'-'}</TableCell>
                                        <TableCell sx={{...colStyle, minWidth: 200}}>{user.update_time? formatFromReportDateString(user.update_time):'-'}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteClick(user)}
                                                title="Remove User"
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                            <TableCell colSpan={13} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <Collapse
                                                in={expandedMuid === user.muid}
                                                timeout="auto"
                                                unmountOnExit
                                                >
                                                    <Stack spacing={4} px={2} py={4} direction={'row'} sx={{
                                                        minHeight: '120px',
                                                        height: 'auto',
                                                        maxHeight: 'max-content'  
                                                    }}>
                                                            <Typography variant="h3" alignSelf={'center'} maxWidth={100} minWidth={100}>
                                                                User Details
                                                            </Typography>
                                                            <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                <Typography variant="body2" fontWeight={'bold'}>
                                                                Date of Birth :&nbsp; 
                                                                </Typography>
                                                                <Typography variant="body2"> 
                                                                    {user.date_of_birth? formatFromReportDateString(user.date_of_birth) : 'None'}
                                                                </Typography>
                                                            </Paper>
                                                            <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                <Typography variant="body2" fontWeight={'bold'}>
                                                                Additional First Name :&nbsp; 
                                                                </Typography>
                                                                <Typography variant="body2"> 
                                                                    {user.additional_first_name? user.additional_first_name : 'None'}
                                                                </Typography>
                                                            </Paper>
                                                            <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                <Typography variant="body2" fontWeight={'bold'}>
                                                                    Maiden Name:&nbsp;
                                                                </Typography>
                                                                <Typography variant="body2"> 
                                                                    {user.maiden_name ? user.maiden_name: 'None'}
                                                                </Typography>
                                                            </Paper>
                                                            {formatAddresses(user.addresses)}
                                                            {formatStringArray(user.phones, 'Phone Number')}
                                                            {formatStringArray(user.additional_emails, 'Additional Email')}
                                                    </Stack>
                                                    {user.subscription && user.subscription!=='Individual User' && 
                                                    <Stack spacing={4} px={2} py={4} direction={'row'} height={'120px'}>
                                                        <Typography variant="h3" alignSelf={'center'} maxWidth={100} minWidth={100}>
                                                                {FormatLinkedAccountType[user.subscription as keyof typeof FormatLinkedAccountType]} Details
                                                        </Typography>
                                                        {(user.subscription ==='Parent Family User' || user.subscription ==='Child Family User') && <>
                                                                <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                    <Typography variant="body2" fontWeight={'bold'}>
                                                                        {FormatLinkedAccountID[user.subscription as keyof typeof FormatLinkedAccountID]} : 
                                                                    </Typography>
                                                                    <Typography variant="body2"> 
                                                                        {user.linked_muid? user.linked_muid : 'None'}
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                    <Typography variant="body2" fontWeight={'bold'}>
                                                                        {FormatLinkedAccountType[user.subscription as keyof typeof FormatLinkedAccountType]}email address:&nbsp;
                                                                    </Typography>
                                                                    <Typography variant="body2"> 
                                                                        {user.linked_email ? user.linked_email: 'None'}
                                                                    </Typography>
                                                                </Paper>
                                                            </>}
                                                            {
                                                                user.subscription === 'Corporate Grant User' &&
                                                                <Paper sx={{display:'flex', alignItems:'center', padding:'20px',...colStyle}}>
                                                                    <Typography variant="body2" fontWeight={'bold'}>
                                                                        Corporate code :&nbsp;
                                                                    </Typography>
                                                                    <Typography variant="body2"> 
                                                                        {user.linked_corp_code ? user.linked_corp_code : 'None'}
                                                                    </Typography>
                                                                </Paper>
                                                            }
                                                            <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                <Typography variant="body2" fontWeight={'bold'}>
                                                                    {FormatLinkedAccountType[user.subscription as keyof typeof FormatLinkedAccountType]}created at :&nbsp;
                                                                </Typography>
                                                                <Typography variant="body2"> 
                                                                    {user.linked_create_time ? formatFromReportDateString(user.linked_create_time) : 'None'}
                                                                </Typography>
                                                            </Paper>
                                                            <Paper sx={{display:'flex', alignItems:'center', padding:'20px', ...colStyle}}>
                                                                <Typography variant="body2" fontWeight={'bold'}>
                                                                    {FormatLinkedAccountType[user.subscription as keyof typeof FormatLinkedAccountType]} subscription expires at :&nbsp; 
                                                                </Typography>
                                                                <Typography variant="body2"> 
                                                                    {user.linked_expire_time ? formatFromReportDateString(user.linked_expire_time) : 'None'}
                                                                </Typography>
                                                            </Paper>
                                                    </Stack>}
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <CustomTablePagination
                            onPageChange={(newPage: number) => getNextPage(newPage)}
                            onPageSizeChange={(pageSize: number) =>
                                setNewPageSize(pageSize)
                            }
                            page={page}
                            pageSize={pageSize}
                            hasNext={hasNext}
                    />
                </>
            }
            <Modal
                open={dialog.open}
                onClose={handleDeleteCancel}
                sx={{
                    backdropFilter: 'blur(5px)',
                    background: 'rgba(0, 0, 0, 0.01)',
                }}
            >
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        outline: 'none',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        maxWidth: '650px',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        background: '#fff',
                        p: 6,
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    }}
                >
                    <IconButton
                        color="inherit"
                        onClick={handleDeleteCancel}
                        disabled={dialog.isDeleting}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            backgroundColor: '#fff',
                            borderRadius: 0,
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Stack spacing={4} width="100%" alignItems="center">
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: '#FFF3E0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <WarningAmber sx={{ fontSize: 44, color: '#FF9800' }} />
                        </Box>

                        <Typography variant="h2" textAlign="center" sx={{ fontWeight: 600 }}>
                            Remove User
                        </Typography>

                        <Typography variant="body1" textAlign="center" color="#615F6B">
                            Are you sure you want to remove this user? This action cannot be undone.
                        </Typography>

                        <Box
                            sx={{
                                width: '100%',
                                backgroundColor: '#F8F9FA',
                                borderRadius: '12px',
                                p: 4,
                            }}
                        >
                            <Stack spacing={2.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" color="#615F6B" fontWeight={500}>
                                        MUID
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>
                                        {dialog.userToDelete?.muid}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" color="#615F6B" fontWeight={500}>
                                        Email
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>
                                        {dialog.userToDelete?.email || 'N/A'}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" color="#615F6B" fontWeight={500}>
                                        Full Name
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>
                                        {dialog.userToDelete?.name || 'N/A'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Collapse in={dialog.otpSent} sx={{ width: '100%' }}>
                            <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                {/* Circular countdown timer */}
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={(dialog.otpSecondsLeft / OTP_DURATION) * 100}
                                        size={64}
                                        thickness={4}
                                        sx={{
                                            color: otpExpired ? '#d32f2f' : '#426BF0',
                                            transform: 'rotate(-90deg) !important',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={600}
                                            color={otpExpired ? '#d32f2f' : '#3A155A'}
                                        >
                                            {formatTime(dialog.otpSecondsLeft)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {otpExpired ? (
                                    <Typography variant="body2" color="#d32f2f" fontWeight={500}>
                                        Code has expired. Please resend to get a new code.
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="#615F6B" fontWeight={500}>
                                        Enter the 6-digit code sent to your email
                                    </Typography>
                                )}
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <OtpInput
                                        value={dialog.otpCode}
                                        onChange={(val) => { if (/^\d*$/.test(val)) dialogDispatch({ type: 'SET_OTP_CODE', payload: val }) }}
                                        numInputs={6}
                                        inputType="tel"
                                        renderSeparator={<span style={{ width: isXSmallScreen ? '3px' : isSmallScreen ? '4px' : '8px' }} />}
                                        renderInput={(props) => <input {...props} disabled={otpExpired} />}
                                        inputStyle={{
                                            width: isXSmallScreen ? '28px' : isSmallScreen ? '36px' : '48px',
                                            height: isXSmallScreen ? '28px' : isSmallScreen ? '36px' : '48px',
                                            border: `2px solid ${otpExpired ? '#d32f2f' : '#D9E1FC'}`,
                                            borderRadius: '10px',
                                            background: otpExpired ? '#fafafa' : '#fff',
                                            color: otpExpired ? '#999' : '#1A1B20',
                                            fontSize: isXSmallScreen ? '13px' : isSmallScreen ? '15px' : '20px',
                                            fontWeight: 600,
                                        }}
                                    />
                                </Box>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleInitiateRemoval}
                                    disabled={dialog.isSendingOtp || !otpExpired}
                                    sx={{ color: '#426BF0', textTransform: 'none', fontSize: '13px' }}
                                >
                                    {dialog.isSendingOtp ? 'Resending...' : 'Resend'}
                                </Button>
                            </Stack>
                        </Collapse>

                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="darkPurple"
                                    onClick={handleDeleteCancel}
                                    disabled={dialog.isDeleting || dialog.isSendingOtp}
                                    sx={{
                                        borderRadius: '10px',
                                        boxShadow: 'none',
                                        border: '2px solid #426BF0',
                                        background: 'transparent',
                                        color: '#426BF0',
                                        py: 2,
                                        '&:hover': {
                                            background: 'rgba(66, 107, 240, 0.08)',
                                        },
                                        '&.Mui-disabled': {
                                            background: 'transparent',
                                            color: '#426BF0',
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                {!dialog.otpSent ? (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleInitiateRemoval}
                                        disabled={dialog.isSendingOtp}
                                        sx={{
                                            borderRadius: '10px',
                                            boxShadow: 'none',
                                            backgroundColor: '#426BF0',
                                            color: '#fff',
                                            py: 2,
                                            '&:hover': {
                                                backgroundColor: '#3556C0',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#426BF0',
                                                color: '#fff',
                                                opacity: 0.6,
                                            },
                                        }}
                                    >
                                        {dialog.isSendingOtp ? 'Sending OTP...' : 'Initiate Removal'}
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleDeleteConfirm}
                                        disabled={dialog.isDeleting || dialog.otpCode.length !== 6 || otpExpired}
                                        sx={{
                                            borderRadius: '10px',
                                            boxShadow: 'none',
                                            backgroundColor: '#d32f2f',
                                            color: '#fff',
                                            py: 2,
                                            '&:hover': {
                                                backgroundColor: '#b71c1c',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#d32f2f',
                                                color: '#fff',
                                                opacity: 0.6,
                                            },
                                        }}
                                    >
                                        {dialog.isDeleting ? 'Removing...' : 'Remove User'}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Stack>
                </Grid>
            </Modal>
        </Box>
    )
}